import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, IconButton, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Divider, Tooltip, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, writeBatch, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import LagbasLogo from '../components/LagbasLogo';

const THEME_COLORS = [
  '#FF6B00', '#1565C0', '#2E7D32', '#6A1B9A',
  '#C62828', '#00838F', '#E65100', '#37474F',
  '#FF8F00', '#00695C', '#AD1457', '#283593',
];

const EMPTY_CAMPAIGN = { name: '', description: '', productId: '', productName: '' };
const EMPTY_THEME = {
  name: '', description: '', color: THEME_COLORS[0],
  occurrences: 4, intervalType: 'weeks', intervalValue: 1,
  startDate: '', assignedToUid: '', assignedToName: '',
  productId: '', productName: '',
  reminders: [],
};

function generateDates(startDate, occurrences, intervalType, intervalValue) {
  const dates = [];
  const start = new Date(startDate + 'T12:00:00');
  for (let i = 0; i < occurrences; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i * intervalValue * (intervalType === 'weeks' ? 7 : 1));
    dates.push(d);
  }
  return dates;
}

export default function LinkedInKampanjer() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [campDialog, setCampDialog] = useState(false);
  const [campForm, setCampForm] = useState(EMPTY_CAMPAIGN);
  const [editingCampId, setEditingCampId] = useState(null);
  const [campSaving, setCampSaving] = useState(false);

  const [themeDialog, setThemeDialog] = useState(false);
  const [themeForm, setThemeForm] = useState(EMPTY_THEME);
  const [editingThemeId, setEditingThemeId] = useState(null);
  const [themeSaving, setThemeSaving] = useState(false);

  const [generating, setGenerating] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [importThemeDialog, setImportThemeDialog] = useState(false);
  const [importableLabels, setImportableLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [importingThemes, setImportingThemes] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    load();
    loadUsers();
    getDocs(query(collection(db, 'products'), orderBy('name'))).then(snap =>
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'linkedinCampaigns'), orderBy('createdAt', 'desc')));
    const cs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setCampaigns(cs);
    if (cs.length > 0 && !selectedCampaign) setSelectedCampaign(cs[0]);
    setLoading(false);
  }

  async function loadUsers() {
    const snap = await getDocs(collection(db, 'users'));
    setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function handleSaveCampaign() {
    if (!campForm.name.trim()) return;
    setCampSaving(true);
    if (editingCampId) {
      await updateDoc(doc(db, 'linkedinCampaigns', editingCampId), {
        name: campForm.name.trim(),
        description: campForm.description.trim(),
        productId: campForm.productId || '',
        productName: campForm.productName || '',
      });
      setCampaigns(prev => prev.map(c => c.id === editingCampId ? { ...c, ...campForm } : c));
      if (selectedCampaign?.id === editingCampId) {
        setSelectedCampaign(prev => ({ ...prev, ...campForm }));
      }
    } else {
      const ref = await addDoc(collection(db, 'linkedinCampaigns'), {
        name: campForm.name.trim(),
        description: campForm.description.trim(),
        productId: campForm.productId || '',
        productName: campForm.productName || '',
        themes: [],
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      const newCamp = { id: ref.id, ...campForm, themes: [], createdBy: currentUser.uid };
      setCampaigns(prev => [newCamp, ...prev]);
      setSelectedCampaign(newCamp);
    }
    setCampSaving(false);
    setCampDialog(false);
  }

  async function handleDeleteCampaign(campaign) {
    await deleteDoc(doc(db, 'linkedinCampaigns', campaign.id));
    setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
    if (selectedCampaign?.id === campaign.id) {
      setSelectedCampaign(campaigns.find(c => c.id !== campaign.id) || null);
    }
    setDeleteConfirm(null);
  }

  async function handleSaveTheme() {
    if (!themeForm.name.trim() || !selectedCampaign) return;
    setThemeSaving(true);
    const themes = selectedCampaign.themes || [];
    let updatedThemes;
    if (editingThemeId) {
      updatedThemes = themes.map(t => t.id === editingThemeId ? { ...t, ...themeForm } : t);
    } else {
      updatedThemes = [...themes, { id: Date.now().toString(), ...themeForm }];
    }
    await updateDoc(doc(db, 'linkedinCampaigns', selectedCampaign.id), { themes: updatedThemes });
    const updated = { ...selectedCampaign, themes: updatedThemes };
    setSelectedCampaign(updated);
    setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? updated : c));
    setThemeSaving(false);
    setThemeDialog(false);
  }

  async function handleDeleteTheme(themeId) {
    const updatedThemes = (selectedCampaign.themes || []).filter(t => t.id !== themeId);
    await updateDoc(doc(db, 'linkedinCampaigns', selectedCampaign.id), { themes: updatedThemes });
    const updated = { ...selectedCampaign, themes: updatedThemes };
    setSelectedCampaign(updated);
    setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? updated : c));
  }

  async function handleGeneratePlaceholders(theme) {
    if (!theme.startDate || !selectedCampaign) return;
    setGenerating(theme.id);
    try {
      const dates = generateDates(theme.startDate, theme.occurrences, theme.intervalType, theme.intervalValue);
      const batch = writeBatch(db);
      const themeReminders = theme.reminders || [];
      for (let i = 0; i < dates.length; i++) {
        const ref = doc(collection(db, 'linkedinPosts'));
        const scheduledDateStr = dates[i].toISOString().split('T')[0];
        batch.set(ref, {
          isPlaceholder: true,
          campaignId: selectedCampaign.id,
          campaignName: selectedCampaign.name,
          productId: theme.productId || selectedCampaign.productId || '',
          productName: theme.productName || selectedCampaign.productName || '',
          themeId: theme.id,
          themeName: theme.name,
          themeColor: theme.color,
          scheduledDate: Timestamp.fromDate(dates[i]),
          assignedToUid: theme.assignedToUid || '',
          assignedToName: theme.assignedToName || '',
          title: `${theme.name} – ${i + 1}/${theme.occurrences}`,
          type: (theme.productId || selectedCampaign.productId) ? 'produkt' : 'tikab',
          status: 'idé',
          content: '',
          summary: '',
          imageUrls: [],
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
        });
        for (const reminder of themeReminders) {
          const rd = new Date(scheduledDateStr + 'T12:00:00');
          rd.setDate(rd.getDate() - (reminder.daysBeforeNum || 0));
          const reminderDateStr = rd.toISOString().split('T')[0];
          const reminderHour = reminder.time ? Number(reminder.time.split(':')[0]) : 8;
          const qRef = doc(collection(db, 'linkedinReminderQueue'));
          batch.set(qRef, {
            postId: ref.id,
            campaignId: selectedCampaign.id,
            campaignName: selectedCampaign.name,
            themeName: theme.name,
            productName: theme.productName || selectedCampaign.productName || '',
            themeColor: theme.color,
            postTitle: `${theme.name} – ${i + 1}/${theme.occurrences}`,
            scheduledDate: scheduledDateStr,
            reminderDate: reminderDateStr,
            reminderHour,
            assignedToUid: theme.assignedToUid || '',
            assignedToName: theme.assignedToName || '',
            sent: false,
            createdAt: serverTimestamp(),
          });
        }
      }
      await batch.commit();

      const updatedThemes = (selectedCampaign.themes || []).map(t =>
        t.id === theme.id ? { ...t, placeholdersGenerated: true, placeholderCount: dates.length } : t
      );
      await updateDoc(doc(db, 'linkedinCampaigns', selectedCampaign.id), { themes: updatedThemes });
      const updated = { ...selectedCampaign, themes: updatedThemes };
      setSelectedCampaign(updated);
      setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? updated : c));
    } finally {
      setGenerating(null);
    }
  }

  async function handleOpenImportThemes() {
    if (!selectedCampaign) return;
    setImportLoading(true);
    setImportThemeDialog(true);
    setSelectedLabels([]);
    try {
      const snap = await getDocs(collection(db, 'linkedinPosts'));
      const existingThemeNames = new Set(
        (selectedCampaign.themes || []).map(t => t.name.toLowerCase().trim())
      );
      const labelCounts = {};
      snap.docs.forEach(d => {
        const data = d.data();
        const label = (data.themeLabel || '').trim();
        if (!label) return;
        // Only include posts that have a free-text label but no campaign theme yet
        if (!data.themeId || !data.themeName) {
          labelCounts[label] = (labelCounts[label] || 0) + 1;
        }
      });
      const labels = Object.entries(labelCounts)
        .filter(([label]) => !existingThemeNames.has(label.toLowerCase()))
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count }));
      setImportableLabels(labels);
      setSelectedLabels(labels.map(l => l.label));
    } finally {
      setImportLoading(false);
    }
  }

  async function handleImportThemes() {
    if (!selectedCampaign || selectedLabels.length === 0) return;
    setImportingThemes(true);
    try {
      const existingCount = (selectedCampaign.themes || []).length;
      const newThemes = selectedLabels.map((label, i) => ({
        id: Date.now().toString() + '_' + i,
        name: label,
        description: '',
        color: THEME_COLORS[(existingCount + i) % THEME_COLORS.length],
        occurrences: 4,
        intervalType: 'weeks',
        intervalValue: 1,
        startDate: '',
        assignedToUid: '',
        assignedToName: '',
        productId: selectedCampaign.productId || '',
        productName: selectedCampaign.productName || '',
        reminders: [],
      }));
      const updatedThemes = [...(selectedCampaign.themes || []), ...newThemes];
      await updateDoc(doc(db, 'linkedinCampaigns', selectedCampaign.id), { themes: updatedThemes });
      const updated = { ...selectedCampaign, themes: updatedThemes };
      setSelectedCampaign(updated);
      setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? updated : c));
      setImportThemeDialog(false);
    } finally {
      setImportingThemes(false);
    }
  }

  const openNewCampaign = () => {
    setCampForm(EMPTY_CAMPAIGN);
    setEditingCampId(null);
    setCampDialog(true);
  };

  const openEditCampaign = (c) => {
    setCampForm({ name: c.name, description: c.description || '', productId: c.productId || '', productName: c.productName || '' });
    setEditingCampId(c.id);
    setCampDialog(true);
  };

  const openNewTheme = () => {
    const nextColor = THEME_COLORS[(selectedCampaign?.themes?.length || 0) % THEME_COLORS.length];
    setThemeForm({
      ...EMPTY_THEME,
      color: nextColor,
      productId: selectedCampaign?.productId || '',
      productName: selectedCampaign?.productName || '',
    });
    setEditingThemeId(null);
    setThemeDialog(true);
  };

  const openEditTheme = (t) => {
    setThemeForm({ ...EMPTY_THEME, ...t });
    setEditingThemeId(t.id);
    setThemeDialog(true);
  };

  const previewDates = themeForm.startDate && themeForm.occurrences
    ? generateDates(themeForm.startDate, Math.min(themeForm.occurrences, 5), themeForm.intervalType, themeForm.intervalValue)
    : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/marketing/linkedin')}>
          <ArrowBackIcon />
        </IconButton>
        <LinkedInIcon sx={{ color: '#0a66c2', fontSize: '1.4rem' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">Innehållskampanjer</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Planera teman och fyll kalendern med platshållare
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewCampaign}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700, whiteSpace: 'nowrap' }}>
          Ny kampanj
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress sx={{ color: '#FF6B00' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 56px)' }}>
          {/* Left: campaign list */}
          <Box sx={{
            width: 260, flexShrink: 0, borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper', py: 2, px: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5,
          }}>
            {campaigns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CampaignIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">Inga kampanjer ännu</Typography>
              </Box>
            ) : campaigns.map(c => (
              <Paper key={c.id} elevation={0}
                onClick={() => setSelectedCampaign(c)}
                sx={{
                  p: 1.5, borderRadius: 1.5, cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selectedCampaign?.id === c.id ? '#FF6B00' : 'divider',
                  bgcolor: selectedCampaign?.id === c.id ? 'rgba(255,107,0,0.08)' : 'background.paper',
                  '&:hover': { borderColor: '#FF6B00', bgcolor: 'rgba(255,107,0,0.08)' },
                  transition: 'all 0.15s',
                }}>
                <Typography variant="body2" fontWeight={700} noWrap>{c.name}</Typography>
                {c.productName && (
                  <Chip label={c.productName} size="small"
                    sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(0,131,143,0.15)', color: '#00838F', fontWeight: 600, mt: 0.25 }} />
                )}
                {c.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                    {c.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                  {(c.themes || []).map(t => (
                    <Box key={t.id} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color }} />
                  ))}
                  {(c.themes || []).length === 0 && (
                    <Typography variant="caption" color="text.disabled">Inga teman</Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Right: selected campaign detail */}
          <Box sx={{ flex: 1, p: 3 }}>
            {!selectedCampaign ? (
              <Box sx={{ textAlign: 'center', pt: 8 }}>
                <CampaignIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">Välj eller skapa en kampanj</Typography>
              </Box>
            ) : (
              <>
                {/* Campaign header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h5" fontWeight={800}>{selectedCampaign.name}</Typography>
                      {selectedCampaign.productName && (
                        <Chip label={selectedCampaign.productName} size="small"
                          sx={{ bgcolor: 'rgba(0,131,143,0.15)', color: '#00838F', fontWeight: 700, fontSize: '0.72rem' }} />
                      )}
                      <Tooltip title="Redigera kampanj">
                        <IconButton size="small" onClick={() => openEditCampaign(selectedCampaign)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ta bort kampanj">
                        <IconButton size="small" onClick={() => setDeleteConfirm(selectedCampaign)}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {selectedCampaign.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {selectedCampaign.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Tooltip title="Importera teman från idéer i LinkedInverkstaden">
                      <Button variant="outlined" startIcon={<FileDownloadIcon />}
                        onClick={handleOpenImportThemes}
                        sx={{ borderColor: 'divider', color: 'text.secondary',
                          '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                          fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        Importera teman
                      </Button>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewTheme}
                      sx={{ borderColor: '#FF6B00', color: '#FF6B00',
                        '&:hover': { bgcolor: 'rgba(255,107,0,0.08)', borderColor: '#FF6B00' },
                        fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Nytt tema
                    </Button>
                  </Box>
                </Box>

                {/* Themes */}
                {(selectedCampaign.themes || []).length === 0 ? (
                  <Paper elevation={0} sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center' }}>
                    <AutoAwesomeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>Inga teman ännu</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                      Lägg till ett tema och sätt hur ofta det ska publiceras
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTheme}
                      sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                      Lägg till tema
                    </Button>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(selectedCampaign.themes || []).map(theme => (
                      <ThemeCard key={theme.id} theme={theme}
                        campaignProductId={selectedCampaign.productId}
                        onEdit={() => openEditTheme(theme)}
                        onDelete={() => handleDeleteTheme(theme.id)}
                        onGenerate={() => handleGeneratePlaceholders(theme)}
                        generating={generating === theme.id}
                        onViewCalendar={() => navigate('/marketing/linkedin?view=calendar')}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Import themes dialog */}
      <Dialog open={importThemeDialog} onClose={() => setImportThemeDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Importera teman från idéer
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {importLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FF6B00' }} />
            </Box>
          ) : importableLabels.length === 0 ? (
            <Alert severity="info">
              Inga nya teman hittades. Antingen har alla idéer redan fått kampanjteman, eller saknar de fritext-tema.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Följande teman hittades bland idéer som saknar kampanjtema. Välj vilka som ska läggas till i <strong>{selectedCampaign?.name}</strong>.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {importableLabels.map(({ label, count }) => (
                  <FormControlLabel key={label}
                    control={
                      <Checkbox
                        checked={selectedLabels.includes(label)}
                        onChange={e => setSelectedLabels(prev =>
                          e.target.checked ? [...prev, label] : prev.filter(l => l !== label)
                        )}
                        size="small"
                        sx={{ '&.Mui-checked': { color: '#FF6B00' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{label}</Typography>
                        <Chip label={`${count} idé${count !== 1 ? 'er' : ''}`} size="small"
                          sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'action.hover' }} />
                      </Box>
                    }
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button size="small" onClick={() => setSelectedLabels(importableLabels.map(l => l.label))}>
                  Markera alla
                </Button>
                <Button size="small" onClick={() => setSelectedLabels([])}>
                  Avmarkera alla
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setImportThemeDialog(false)}>Avbryt</Button>
          {importableLabels.length > 0 && (
            <Button variant="contained" onClick={handleImportThemes}
              disabled={importingThemes || selectedLabels.length === 0}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              {importingThemes ? 'Importerar...' : `Importera ${selectedLabels.length} tema${selectedLabels.length !== 1 ? 'n' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Campaign dialog */}
      <Dialog open={campDialog} onClose={() => setCampDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {editingCampId ? 'Redigera kampanj' : 'Ny kampanj'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="Kampanjnamn *" autoFocus
              value={campForm.name}
              onChange={e => setCampForm(f => ({ ...f, name: e.target.value }))}
              placeholder="T.ex. Flatbox, Centralstaden 2026" />
            <TextField select fullWidth label="Kopplad produkt/tjänst (valfritt)"
              value={campForm.productId || ''}
              onChange={e => {
                const p = products.find(x => x.id === e.target.value);
                setCampForm(f => ({ ...f, productId: e.target.value, productName: p?.name || '' }));
              }}>
              <MenuItem value="">– Ingen koppling –</MenuItem>
              {products.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Beskrivning" multiline rows={2}
              value={campForm.description}
              onChange={e => setCampForm(f => ({ ...f, description: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCampDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSaveCampaign}
            disabled={campSaving || !campForm.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {campSaving ? 'Sparar...' : editingCampId ? 'Spara' : 'Skapa kampanj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Theme dialog */}
      <Dialog open={themeDialog} onClose={() => setThemeDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: themeForm.color, flexShrink: 0 }} />
            {editingThemeId ? 'Redigera tema' : 'Nytt tema'}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {selectedCampaign?.name}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Name + color */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField label="Temanamn *" fullWidth autoFocus
                  value={themeForm.name}
                  onChange={e => setThemeForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="T.ex. Enkelhet, Kundcase, Tips & tricks" />
                <TextField select fullWidth
                  label={selectedCampaign?.productId && themeForm.productId === selectedCampaign.productId ? 'Produkt (ärvd från kampanj)' : 'Kopplad produkt/tjänst'}
                  value={themeForm.productId || ''}
                  onChange={e => {
                    const p = products.find(x => x.id === e.target.value);
                    setThemeForm(f => ({ ...f, productId: e.target.value, productName: p?.name || '' }));
                  }}
                  sx={selectedCampaign?.productId && themeForm.productId === selectedCampaign.productId ? {
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00838F44' },
                    '& .MuiInputLabel-root': { color: '#00838F' },
                  } : {}}>
                  <MenuItem value="">– Ingen koppling –</MenuItem>
                  {products.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{selectedCampaign?.productId === p.id ? ' ✓ kampanjens produkt' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Färg</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, maxWidth: 120 }}>
                  {THEME_COLORS.map(c => (
                    <Box key={c} onClick={() => setThemeForm(f => ({ ...f, color: c }))}
                      sx={{
                        width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                        border: themeForm.color === c ? '2px solid' : '2px solid transparent',
                        borderColor: themeForm.color === c ? 'text.primary' : 'transparent',
                        outline: themeForm.color === c ? '2px solid white' : 'none',
                        outlineOffset: '-3px',
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.2)' },
                      }} />
                  ))}
                </Box>
              </Box>
            </Box>  {/* end name+color row */}

            <TextField fullWidth label="Beskrivning" multiline rows={2}
              value={themeForm.description}
              onChange={e => setThemeForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Vad handlar temat om? Vad är budskapet?" />

            {/* Frequency */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                Frekvens
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <TextField label="Antal inlägg" type="number" sx={{ width: 130 }}
                  inputProps={{ min: 1, max: 52 }}
                  value={themeForm.occurrences}
                  onChange={e => setThemeForm(f => ({ ...f, occurrences: Math.max(1, Math.min(52, Number(e.target.value))) }))} />
                <TextField label="Var" type="number" sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 52 }}
                  value={themeForm.intervalValue}
                  onChange={e => setThemeForm(f => ({ ...f, intervalValue: Math.max(1, Number(e.target.value)) }))} />
                <TextField select label="Enhet" sx={{ width: 120 }}
                  value={themeForm.intervalType}
                  onChange={e => setThemeForm(f => ({ ...f, intervalType: e.target.value }))}>
                  <MenuItem value="days">dag(ar)</MenuItem>
                  <MenuItem value="weeks">vecka/or</MenuItem>
                </TextField>
              </Box>
            </Box>

            {/* Startdate + assignee */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="Startdatum" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                value={themeForm.startDate}
                onChange={e => setThemeForm(f => ({ ...f, startDate: e.target.value }))} />
              <TextField select label="Ansvarig skapare" sx={{ flex: 1 }}
                value={themeForm.assignedToUid || ''}
                onChange={e => {
                  const u = users.find(x => x.id === e.target.value);
                  setThemeForm(f => ({
                    ...f,
                    assignedToUid: e.target.value,
                    assignedToName: u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : '',
                  }));
                }}>
                <MenuItem value="">– Ingen –</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Preview dates */}
            {previewDates.length > 0 && (
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                  Förhandsgranskning av datum
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {previewDates.map((d, i) => (
                    <Chip key={i}
                      label={d.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}
                      size="small"
                      sx={{ bgcolor: themeForm.color + '22', color: themeForm.color, fontWeight: 600, border: `1px solid ${themeForm.color}44`, fontSize: '0.72rem' }}
                    />
                  ))}
                  {themeForm.occurrences > 5 && (
                    <Chip label={`+${themeForm.occurrences - 5} till`} size="small"
                      sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontSize: '0.72rem' }} />
                  )}
                </Box>
              </Box>
            )}

            {/* E-postpåminnelser */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  E-postpåminnelser till ansvarig
                </Typography>
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setThemeForm(f => ({
                    ...f, reminders: [...(f.reminders || []), { daysBeforeNum: 7, time: '08:00' }]
                  }))}>
                  Lägg till
                </Button>
              </Box>
              {(themeForm.reminders || []).length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  Inga påminnelser – den ansvarige får inget mail automatiskt
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(themeForm.reminders || []).map((r, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField label="Dagar innan" type="number" size="small" sx={{ width: 115 }}
                        inputProps={{ min: 0, max: 365 }}
                        value={r.daysBeforeNum}
                        onChange={e => setThemeForm(f => {
                          const rem = [...f.reminders];
                          rem[idx] = { ...rem[idx], daysBeforeNum: Math.max(0, Number(e.target.value)) };
                          return { ...f, reminders: rem };
                        })} />
                      <TextField label="Kl." type="time" size="small" sx={{ width: 105 }}
                        InputLabelProps={{ shrink: true }}
                        value={r.time}
                        onChange={e => setThemeForm(f => {
                          const rem = [...f.reminders];
                          rem[idx] = { ...rem[idx], time: e.target.value };
                          return { ...f, reminders: rem };
                        })} />
                      <Tooltip title="Ta bort påminnelse">
                        <IconButton size="small" onClick={() => setThemeForm(f => ({
                          ...f, reminders: f.reminders.filter((_, i) => i !== idx),
                        }))}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setThemeDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSaveTheme}
            disabled={themeSaving || !themeForm.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {themeSaving ? 'Sparar...' : editingThemeId ? 'Spara tema' : 'Lägg till tema'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete campaign confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight="bold">Ta bort kampanj?</DialogTitle>
        <DialogContent>
          <Typography>"{deleteConfirm?.name}" tas bort. Platshållare i kalendern tas inte bort automatiskt.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Avbryt</Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteCampaign(deleteConfirm)}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ThemeCard({ theme, campaignProductId, onEdit, onDelete, onGenerate, generating, onViewCalendar }) {
  const productInherited = theme.productId && theme.productId === campaignProductId;
  return (
    <Paper elevation={0} sx={{
      border: '1px solid', borderColor: theme.color + '44',
      borderLeft: `4px solid ${theme.color}`,
      borderRadius: 2, p: 2.5,
      bgcolor: theme.color + '08',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.color, flexShrink: 0 }} />
            <Typography variant="body1" fontWeight={700}>{theme.name}</Typography>
            {theme.productName && (
              <Chip label={theme.productName} size="small"
                sx={{
                  height: 18, fontSize: '0.65rem', fontWeight: 700,
                  bgcolor: productInherited ? 'rgba(0,131,143,0.15)' : 'rgba(106,27,154,0.12)',
                  color: productInherited ? '#00838F' : '#6A1B9A',
                  border: productInherited ? '1px solid rgba(0,131,143,0.3)' : '1px solid rgba(106,27,154,0.25)',
                }}
                title={productInherited ? 'Ärvd från kampanjen' : 'Temats produkt'}
              />
            )}
            {theme.placeholdersGenerated && (
              <Chip icon={<CheckCircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                label={`${theme.placeholderCount || 0} platshållare skapade`} size="small"
                sx={{ bgcolor: theme.color + '22', color: theme.color, fontWeight: 600, fontSize: '0.68rem' }} />
            )}
          </Box>
          {theme.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{theme.description}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {theme.occurrences} inlägg • var {theme.intervalValue} {theme.intervalType === 'weeks' ? 'vecka' : 'dag'}{theme.intervalValue > 1 ? (theme.intervalType === 'weeks' ? 'r' : 'ar') : ''}
              </Typography>
            </Box>
            {theme.startDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Start: {new Date(theme.startDate + 'T00:00:00').toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
              </Box>
            )}
            {theme.assignedToName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{theme.assignedToName}</Typography>
              </Box>
            )}
            {(theme.reminders || []).length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  🔔 {theme.reminders.length} påminnelse{theme.reminders.length !== 1 ? 'r' : ''} per inlägg
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Redigera tema">
              <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Ta bort tema">
              <IconButton size="small" onClick={onDelete}><DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></IconButton>
            </Tooltip>
          </Box>
          {theme.startDate && (
            <Button size="small" variant="contained"
              startIcon={generating ? <CircularProgress size={12} sx={{ color: 'white' }} /> : theme.placeholdersGenerated ? <RefreshIcon sx={{ fontSize: 14 }} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              onClick={onGenerate}
              disabled={generating}
              sx={{
                bgcolor: theme.color, '&:hover': { bgcolor: theme.color, filter: 'brightness(0.9)' },
                fontSize: '0.72rem', fontWeight: 700, mt: 0.5, whiteSpace: 'nowrap',
              }}>
              {generating ? 'Genererar...' : theme.placeholdersGenerated ? 'Regenerera' : 'Generera platshållare'}
            </Button>
          )}
          {theme.placeholdersGenerated && (
            <Button size="small" startIcon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
              onClick={onViewCalendar}
              sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.25 }}>
              Visa i kalender
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
